import { MailOutlined, PlusOutlined, SaveOutlined, TeamOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { EntityMeta, FieldMeta } from "@wms/domain";
import { Alert, App, Button, Card, Col, DatePicker, Descriptions, Form, Input, Modal, Radio, Row, Select, Space, Table, Tabs, Tag, Typography } from "antd";
import type { FormInstance } from "antd";
import dayjs from "dayjs";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, describeError, fieldErrors } from "../api/client";
import type { Page } from "../api/client";
import { useAuthStore } from "../auth/store";
import { HelpHint } from "../components/HelpHint";
import { checkProblem, labelFor, switchNotice, visibleOptions } from "../components/kzRequisites";
import { PageHeader } from "../components/PageHeader";
import { RefSelect, useRefRows } from "../components/RefSelect";

type Company = Record<string, string | null>;

/**
 * The settings card «Моя компания» is the default organization of «Мои организации» (ТЗ реквизитов §8.1, S02): one row,
 * one set of rules. The labels, help texts and options come from the meta of `companies`; the order is the card's.
 */
const COMPANY_FIELDS: { name: string; label: string; span?: number; placeholder?: string; extra?: string; group?: string }[] = [
  { name: "name", label: "Наименование компании", span: 2 },
  // KZ 30.10.2026 (ТЗ реквизитов §5.1): the form ТОО / ИП and the identifier whose label follows it — at the top
  { name: "kind", label: "Форма" },
  { name: "bin", label: "БИН / ИИН", placeholder: "12 цифр" },
  // UX3010-13: added next to the old keys of the card
  { name: "full_name", label: "Полное наименование", span: 2 },
  { name: "legal_address", label: "Юридический адрес", span: 2 },
  { name: "actual_address", label: "Фактический адрес", span: 2 },
  { name: "phone", label: "Телефон" },
  { name: "email", label: "Email" },
  // UX3010-14: an optional contact; «t.me/name» is accepted and saved as «@name»
  { name: "telegram", label: "Ник в Telegram", placeholder: "@name", extra: "Необязательно: @name или ссылка t.me/name" },
  { name: "director", label: "Руководитель" },
  { name: "accountant", label: "Главный бухгалтер" },
  // ТЗ 1.2 раздел 12.2: the bank requisites of the performer go onto the invoice snapshot
  { name: "bank_name", label: "Банк", span: 2, group: "Банк" },
  { name: "bank_bic", label: "БИК банка", group: "Банк" },
  { name: "bank_account", label: "ИИК (счёт в банке)", group: "Банк" },
  { name: "kbe", label: "КБе", group: "Банк" },
  // ТЗ реквизитов §12.2: the stated VAT status; the certificate is not issued since 01.01.2026 — history only
  { name: "vat_status", label: "Статус по НДС", group: "НДС" },
  { name: "vat_status_since", label: "Статус по НДС действует с", group: "НДС" },
  { name: "vat_status_source", label: "Источник и дата проверки статуса", span: 2, group: "НДС" },
  { name: "vat_certificate", label: "Свидетельство НДС (прежние сведения)", span: 2, group: "НДС" },
];

/** «БИН» / «ИИН» by the chosen form, with the help of the meta. */
function CompanyLabel({ name, fallback, meta, form }: { name: string; fallback: string; meta?: FieldMeta; form: FormInstance }) {
  const kind = Form.useWatch("kind", { form, preserve: true });
  const label = meta ? labelFor(meta, kind) : fallback;
  const help = meta?.label_by?.help?.[String(kind ?? "")] ?? meta?.help ?? null;
  return (
    <span data-testid={`company-label-${name}`}>
      {label}
      {help && <HelpHint subject={label} text={help} size={13} />}
    </span>
  );
}

/** §5.2: the form changed while the identifier is filled — the identifier is not rewritten, the warning says why. */
function CompanySwitchNotice({ meta, form, saved }: { meta?: FieldMeta; form: FormInstance; saved: unknown }) {
  const kind = Form.useWatch("kind", { form, preserve: true });
  const bin = Form.useWatch("bin", { form, preserve: true });
  const typedFor = useRef<unknown>(saved);
  const lastBin = useRef<unknown>(bin);
  useEffect(() => {
    if (bin !== lastBin.current) {
      lastBin.current = bin;
      typedFor.current = kind;
    }
  }, [bin, kind]);
  useEffect(() => {
    typedFor.current = saved;
  }, [saved]);
  const notice = meta ? switchNotice(meta, typedFor.current, kind, bin) : null;
  return notice ? <Alert type="warning" showIcon style={{ marginTop: 6 }} message={notice} data-testid="company-kind-notice" /> : null;
}

interface UserRow {
  id: string;
  username: string;
  full_name: string;
  email: string | null;
  phone?: string | null;
  is_active: boolean;
  must_change_password?: boolean;
  roles?: { code: string; name: string }[];
  warehouse_ids?: string[];
}

interface Invited {
  id: string;
  username: string;
  temporary_password: string;
  email_queued: boolean;
}

/** REQ-01 «Моя компания»: requisites, employees with invitations, roles — one place for the owner of the installation. */
export function CompanyPage() {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const can = useAuthStore((s) => s.can);
  const [form] = Form.useForm();
  const [inviteForm] = Form.useForm();
  const [inviteOpen, setInviteOpen] = useState(false);
  const [invited, setInvited] = useState<Invited | null>(null);

  const company = useQuery({ queryKey: ["company"], queryFn: () => api.get<Company>("/company"), enabled: can("core.settings.read") });
  // the labels, help texts and options of the card come from the meta of «Мои организации» — one description
  const companyMeta = useQuery({ queryKey: ["meta", "companies"], queryFn: () => api.get<EntityMeta>("/companies/meta"), staleTime: 300_000 });
  const metaOf = (name: string) => companyMeta.data?.fields.find((f) => f.name === name);
  const users = useQuery({ queryKey: ["users", "company"], queryFn: () => api.get<Page<UserRow>>("/users", { size: 200, sort: "full_name" }) });
  const roles = useRefRows("roles");
  const warehouses = useRefRows("warehouses");
  useEffect(() => {
    if (company.data) form.setFieldsValue({ ...company.data, vat_status_since: company.data.vat_status_since ? dayjs(company.data.vat_status_since) : undefined });
  }, [company.data, form]);

  const save = useMutation({
    mutationFn: (values: Record<string, unknown>) => {
      const body: Record<string, unknown> = {};
      for (const f of COMPANY_FIELDS) {
        if (!(f.name in values)) continue;
        const value = values[f.name];
        body[f.name] = dayjs.isDayjs(value) ? value.format("YYYY-MM-DD") : value === undefined || value === "" ? null : value;
      }
      return api.put<Company>("/company", body);
    },
    onSuccess: () => {
      message.success("Карточка компании сохранена");
      queryClient.invalidateQueries({ queryKey: ["company"] });
      queryClient.invalidateQueries({ queryKey: ["companies"] });
    },
    onError: (e) => {
      // ТЗ реквизитов §6.1: the server applies the same policy as «Мои организации» — the error goes next to its field
      const errors = fieldErrors(e);
      if (errors.length) form.setFields(errors.map((x) => ({ name: x.field, errors: [x.message] })));
      message.error(describeError(e), 6);
    },
  });

  const companyControl = (name: string, placeholder?: string) => {
    const meta = metaOf(name);
    if (name === "kind") {
      return <Radio.Group optionType="button" buttonStyle="solid" options={visibleOptions(meta?.options ?? [{ value: "too", label: "ТОО" }, { value: "ie", label: "ИП" }], company.data?.kind).map((o) => ({ value: o.value, label: o.label }))} data-testid="company-kind" />;
    }
    if (name === "vat_status") return <Select allowClear options={meta?.options ?? []} placeholder="Не указан" />;
    if (name === "vat_status_since") return <DatePicker format="DD.MM.YYYY" style={{ width: "100%" }} />;
    return <Input allowClear placeholder={placeholder ?? meta?.placeholder ?? undefined} />;
  };
  const companyRules = (name: string) => {
    const meta = metaOf(name);
    if (!meta?.check) return [];
    return [
      {
        validator: (_: unknown, value: unknown) => {
          const saved = company.data?.[name];
          if (saved && String(saved) === String(value ?? "")) return Promise.resolve();
          const problem = checkProblem(meta.check, value, form.getFieldValue("kind"));
          return problem ? Promise.reject(new Error(problem)) : Promise.resolve();
        },
      },
    ];
  };
  const invite = useMutation({
    mutationFn: (values: Record<string, unknown>) => api.post<Invited>("/users/invite", values),
    onSuccess: (data) => {
      setInvited(data);
      inviteForm.resetFields();
      queryClient.invalidateQueries({ queryKey: ["users"] });
    },
    onError: (e) => {
      const errors = fieldErrors(e);
      if (errors.length) inviteForm.setFields(errors.map((x) => ({ name: x.field, errors: [x.message] })));
      message.error(describeError(e), 6);
    },
  });

  const roleName = (code: string) => roles.data?.items.find((r) => r.code === code)?.name ?? code;
  const warehouseName = (id: string) => {
    const w = warehouses.data?.items.find((r) => String(r.id) === id);
    return w ? String(w.code) : id;
  };

  const companyTab = (
    <Card>
      {!can("core.settings.write") && <Alert type="info" showIcon style={{ marginBottom: 16 }} message="Реквизиты меняет администратор" />}
      {/* UX3010-13 (ADR 0018): this card is the default organization of «Мои организации»; the others live there */}
      <Typography.Paragraph type="secondary" data-testid="company-default-note">
        Реквизиты этой карточки сохраняются и в организации по умолчанию. Другие наши организации — в справочнике{" "}
        <Link to="/companies">«Мои организации»</Link>.
      </Typography.Paragraph>
      <Form form={form} layout="vertical" disabled={!can("core.settings.write")} onFinish={(values) => save.mutate(values)}>
        <Row gutter={16}>
          {COMPANY_FIELDS.map((f, i) => {
            const meta = metaOf(f.name);
            const heading = f.group && COMPANY_FIELDS[i - 1]?.group !== f.group;
            return [
              heading ? (
                <Col key={`group-${f.group}`} xs={24}>
                  <Typography.Title level={5} style={{ marginTop: 4 }}>
                    {f.group}
                  </Typography.Title>
                </Col>
              ) : null,
              <Col key={f.name} xs={24} md={f.span === 2 ? 24 : 12} data-testid={`company-field-${f.name}`}>
                <Form.Item
                  name={f.name}
                  label={<CompanyLabel name={f.name} fallback={meta && !meta.label_by ? meta.label : f.label} meta={meta} form={form} />}
                  extra={f.name === "kind" ? <CompanySwitchNotice meta={meta} form={form} saved={company.data?.kind} /> : f.extra}
                  dependencies={f.name === "bin" ? ["kind"] : undefined}
                  rules={companyRules(f.name)}
                >
                  {companyControl(f.name, f.placeholder)}
                </Form.Item>
              </Col>,
            ];
          })}
        </Row>
        {can("core.settings.write") && (
          <Button type="primary" htmlType="submit" icon={<SaveOutlined />} loading={save.isPending}>
            Сохранить
          </Button>
        )}
      </Form>
    </Card>
  );

  const employeesTab = (
    <Card>
      <Space style={{ width: "100%", justifyContent: "space-between", marginBottom: 12 }} wrap>
        <Typography.Text type="secondary">
          Сотрудник получает логин и временный пароль; при первом входе система попросит задать свой. Роль определяет, что он видит и может делать.
        </Typography.Text>
        <Space>
          {can("core.users.write") && (
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setInviteOpen(true)}>
              Пригласить сотрудника
            </Button>
          )}
          <Link to="/users">
            <Button>Все учётные записи</Button>
          </Link>
        </Space>
      </Space>
      <Table<UserRow>
        size="middle"
        rowKey="id"
        loading={users.isLoading}
        dataSource={users.data?.items ?? []}
        pagination={{ pageSize: 20 }}
        columns={[
          { title: "Сотрудник", dataIndex: "full_name", render: (v: string, r) => <Link to={`/users/${r.id}`}>{v}</Link> },
          { title: "Логин", dataIndex: "username" },
          { title: "Email", dataIndex: "email", render: (v: string | null) => v ?? "—" },
          { title: "Роли", render: (_, r) => (r.roles?.length ? r.roles.map((x) => <Tag key={x.code}>{x.name}</Tag>) : <Typography.Text type="secondary">нет ролей</Typography.Text>) },
          { title: "Склады", render: (_, r) => (r.warehouse_ids?.length ? r.warehouse_ids.map(warehouseName).join(", ") : "все") },
          {
            title: "Состояние",
            render: (_, r) => (!r.is_active ? <Tag color="red">отключён</Tag> : r.must_change_password ? <Tag color="orange">приглашён, пароль не сменён</Tag> : <Tag color="green">работает</Tag>),
          },
        ]}
      />
      <Modal
        title="Пригласить сотрудника"
        open={inviteOpen}
        onCancel={() => {
          setInviteOpen(false);
          setInvited(null);
        }}
        footer={null}
        destroyOnHidden
      >
        {invited ? (
          <Space direction="vertical" style={{ width: "100%" }}>
            <Alert
              type="success"
              showIcon
              message={`Учётная запись создана: логин ${invited.username}`}
              description={
                <Descriptions size="small" column={1}>
                  <Descriptions.Item label="Временный пароль">
                    <Typography.Text copyable code>
                      {invited.temporary_password}
                    </Typography.Text>
                  </Descriptions.Item>
                  <Descriptions.Item label="Письмо">{invited.email_queued ? "поставлено в очередь отправки (нужен настроенный SMTP)" : "не отправлялось"}</Descriptions.Item>
                </Descriptions>
              }
            />
            <Typography.Text type="secondary">Пароль показывается один раз: передайте его сотруднику, при первом входе он задаст свой.</Typography.Text>
            <Button type="primary" onClick={() => setInvited(null)}>
              Пригласить ещё
            </Button>
          </Space>
        ) : (
          <Form form={inviteForm} layout="vertical" onFinish={(values) => invite.mutate(values)} initialValues={{ send_email: true }}>
            <Form.Item name="full_name" label="ФИО" rules={[{ required: true, message: "Введите имя" }]}>
              <Input />
            </Form.Item>
            <Form.Item name="email" label="Email" rules={[{ required: true, type: "email", message: "Введите email" }]} extra="Логин будет взят из части до @, если не указать свой">
              <Input prefix={<MailOutlined />} />
            </Form.Item>
            <Form.Item name="username" label="Логин (необязательно)">
              <Input />
            </Form.Item>
            <Form.Item name="role_codes" label="Роли" rules={[{ required: true, message: "Выберите хотя бы одну роль" }]}>
              <Select mode="multiple" options={(roles.data?.items ?? []).map((r) => ({ value: String(r.code), label: `${r.name} (${roleName(String(r.code))})` }))} optionFilterProp="label" />
            </Form.Item>
            <Form.Item name="warehouse_ids" label="Склады (пусто — все)">
              <RefSelect entity="warehouses" multiple />
            </Form.Item>
            <Form.Item name="send_email" label="Отправить письмо с доступом" valuePropName="checked">
              <Select options={[{ value: true, label: "Да, если настроен SMTP" }, { value: false, label: "Нет, передам пароль сам" }]} />
            </Form.Item>
            <Space style={{ width: "100%", justifyContent: "flex-end" }}>
              <Button onClick={() => setInviteOpen(false)}>Отмена</Button>
              <Button type="primary" htmlType="submit" loading={invite.isPending}>
                Создать и пригласить
              </Button>
            </Space>
          </Form>
        )}
      </Modal>
    </Card>
  );

  const rolesTab = (
    <Card>
      <Typography.Paragraph type="secondary">
        Роль — набор прав: что сотрудник видит и какие действия выполняет. Системные роли поставляются с WMS; свои роли создаются в разделе «Роли».
      </Typography.Paragraph>
      {!can("core.roles.read") && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message="Список ролей и их права открыты администратору"
          description="Чтобы выдать сотруднику другую роль или изменить права, обратитесь к администратору."
        />
      )}
      <Table
        size="middle"
        rowKey="id"
        loading={roles.isLoading}
        dataSource={roles.data?.items ?? []}
        pagination={false}
        columns={[
          { title: "Роль", dataIndex: "name", render: (v: string, r: Record<string, unknown>) => <Link to={`/roles/${r.id}`}>{v}</Link> },
          { title: "Код", dataIndex: "code" },
          { title: "Стартовая страница", dataIndex: "start_page", render: (v: string | null) => v ?? "рабочий стол" },
          { title: "Прав", dataIndex: "permissions", render: (v: string[]) => (Array.isArray(v) ? (v.includes("*") ? "все" : String(v.length)) : "—") },
        ]}
      />
      {can("core.roles.read") ? (
        <Link to="/roles">
          <Button style={{ marginTop: 12 }}>Открыть роли</Button>
        </Link>
      ) : null}
    </Card>
  );

  return (
    <div>
      <PageHeader title="Моя компания" subtitle="Реквизиты компании, сотрудники и их роли" extra={<TeamOutlined style={{ fontSize: 22, color: "var(--wms-muted)" }} />} />
      <Tabs
        items={[
          { key: "company", label: "Компания", children: companyTab },
          { key: "employees", label: `Сотрудники${users.data ? ` (${users.data.total})` : ""}`, children: employeesTab },
          { key: "roles", label: "Роли", children: rolesTab },
        ]}
      />
    </div>
  );
}
